import {
  createRouter,
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { fetchSession, hasActiveSession } from './lib/session';
import { AppLayout } from './layout/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { PipelinePage } from './pages/PipelinePage';
import { TenderDetailPage } from './pages/TenderDetailPage';
import { ClientsPage } from './pages/ClientsPage';
import { AdminPage } from './pages/AdminPage';
import { QuotesPage } from './pages/QuotesPage';
import { InventoryPage } from './pages/InventoryPage';
import { RfisPage } from './pages/RfisPage';
import { SiteVisitsPage } from './pages/SiteVisitsPage';
import { WorkloadPage } from './pages/WorkloadPage';
import { TakeoffsPage } from './pages/TakeoffsPage';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// Authenticated section — `beforeLoad` ensures we have a session, otherwise
// bounce to /login. Better Auth's getSession hits the API; we cache via the
// queryClient so subsequent navigations are instant.
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: AppLayout,
  beforeLoad: async ({ context, location }) => {
    const session = await fetchSession(context.queryClient);
    if (!hasActiveSession(session)) {
      throw redirect({
        to: '/login',
        // `location.search` is a parsed object in TanStack Router — use `href`
        // (pathname + searchStr, no origin) for the post-login return path.
        search: { next: location.href },
      });
    }
  },
});

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/pipeline' });
  },
});

const pipelineRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/pipeline',
  component: PipelinePage,
});

const tenderDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/tenders/$tenderId',
  component: TenderDetailPage,
});

const clientsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/clients',
  component: ClientsPage,
});

const quotesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/quotes',
  component: QuotesPage,
});

const inventoryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/inventory',
  component: InventoryPage,
});

const rfisRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/rfis',
  component: RfisPage,
});

const siteVisitsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/site-visits',
  component: SiteVisitsPage,
});

const workloadRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/workload',
  component: WorkloadPage,
});

const takeoffsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/takeoffs',
  component: TakeoffsPage,
});

const adminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin',
  component: AdminPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    indexRoute,
    pipelineRoute,
    tenderDetailRoute,
    clientsRoute,
    quotesRoute,
    inventoryRoute,
    rfisRoute,
    siteVisitsRoute,
    workloadRoute,
    takeoffsRoute,
    adminRoute,
  ]),
]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
